import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Scale, Loader2, MailCheck, ArrowLeft } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui-kit/Button";
import { Input, Label } from "@/components/ui-kit/Input";

export function ForgotPasswordPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [message, setMessage] = useState("");

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMessage(data?.message || "If that address is registered, a reset link has been sent.");
    } catch (err) {
      // Never reveal anything different on error — keep the generic message.
      setMessage("If that address is registered, a reset link has been sent.");
    } finally {
      setSent(true);
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-background text-foreground">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_-10%,hsl(var(--primary)/0.06),transparent_60%)]" />
      <div className="relative mx-auto grid min-h-screen max-w-md place-items-center px-6">
        <div className="w-full">
          <div className="mb-8 flex flex-col items-center text-center">
            <span className="mb-4 grid h-12 w-12 place-items-center rounded-lg bg-primary text-primary-foreground shadow-card">
              <Scale className="h-6 w-6" />
            </span>
            <h1 className="font-serif text-3xl font-semibold tracking-tight">Reset your password</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Enter your account email and we'll send a reset link.
            </p>
          </div>

          <div className="rounded-lg border border-border bg-surface p-7 shadow-card">
            {sent ? (
              <div className="space-y-4 text-center" data-testid="forgot-password-confirmation">
                <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-primary/10 text-primary">
                  <MailCheck className="h-6 w-6" />
                </span>
                <p className="text-sm text-foreground">{message}</p>
                <p className="text-xs text-muted-foreground">
                  The link expires in 30 minutes. Check your inbox (and spam folder).
                </p>
                <Button variant="secondary" className="w-full" onClick={() => navigate("/login")} data-testid="back-to-login-button">
                  <ArrowLeft className="h-4 w-4" /> Back to sign in
                </Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="fp-email">Email</Label>
                  <Input
                    id="fp-email"
                    type="email"
                    autoComplete="username"
                    data-testid="forgot-password-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@firm.com"
                    required
                  />
                </div>
                <Button type="submit" size="lg" className="w-full" disabled={submitting} data-testid="forgot-password-submit">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Send reset link
                </Button>
                <div className="text-center">
                  <button type="button" onClick={() => navigate("/login")} className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
                    Back to sign in
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
